/**
 * DecisionEase — API Client
 * Handles all communication with the FastAPI backend.
 * All authenticated requests use authFetch() which adds the Bearer token.
 */

import { API_BASE, authFetch, getAccessToken } from "./api/base";

export { authFetch, getAccessToken } from "./api/base";

// ── Types ──────────────────────────────────────────────
export interface ThoughtClusterRef {
  id: string;
  name: string;
  thought_count: number;
}

export interface ThoughtCompression {
  summary: string;
  top_themes: string[];
  what_needs_action?: string | null;
  what_can_wait?: string | null;
  emotional_signal?: string | null;
  focus_recommendation?: string | null;
}

export interface Thought {
  id: string;
  raw_text: string;
  category: string;
  thought_type?: string | null;
  decision_state?: string | null;
  category_confidence?: number;
  tags: string[];
  priority: number;
  is_actionable: boolean;
  extracted_action: string | null;
  mood: string | null;
  mood_confidence?: number;
  emotional_weight?: number;
  ambiguity_score?: number;
  possible_directions?: string[];
  compression?: ThoughtCompression | null;
  source: string;
  is_archived: boolean;
  cluster?: ThoughtClusterRef | null;
  user_corrected?: boolean;
  priority_tier?: string | null;
  recommended_action?: string | null;
  created_at: string;
}

export interface CaptureResponse {
  thought: Thought;
  agent_message: string;
  response_type: string;
}

export interface CaptureItemState {
  id: string;
  raw_text: string;
  source: string;
  current_surface: string;
  status: string;
  committed_object_kind?: string | null;
  committed_object_id?: string | null;
  created_at: string;
}

export interface ClarifiedItemState {
  id: string;
  capture_id: string;
  category: string;
  thought_type?: string | null;
  decision_state?: string | null;
  category_confidence: number;
  tags: string[];
  is_actionable: boolean;
  extracted_action?: string | null;
  mood?: string | null;
  mood_confidence: number;
  emotional_weight: number;
  ambiguity_score: number;
  compression_summary?: string | null;
  top_themes: string[];
  what_needs_action?: string | null;
  what_can_wait?: string | null;
  emotional_signal?: string | null;
  focus_recommendation?: string | null;
  possible_directions: string[];
  recommended_route_kind?: string | null;
  created_at: string;
}

/** U26 — Inferred timing the backend ships on schedule_block proposals.
 *  The frontend uses these directly for the commit start/end so it
 *  doesn't have to re-parse weekday phrases ("Tuesday at 5 PM") that
 *  its local heuristic doesn't recognize. */
export interface ScheduleBlockProposalPayload {
  inferred_start_time?: string | null;
  inferred_end_time?: string | null;
  inferred_duration_minutes?: number | null;
  target_date?: string | null;
  day_label?: string | null;
  preferred_start_hour?: number | null;
  preferred_end_hour?: number | null;
  ideal_start_hour?: number | null;
}

export interface CaptureRouteProposal {
  id: string;
  kind: "task" | "goal" | "decision" | "reference" | "capture_only" | "schedule_block";
  label: string;
  rationale: string;
  authority_level: "auto_allowed" | "light_confirmation" | "explicit_confirmation";
  requires_confirmation: boolean;
  destination_layer: string;
  /** Present on `schedule_block` proposals when the backend parsed
   *  concrete timing from the capture text. */
  payload?: ScheduleBlockProposalPayload | null;
}

export interface CaptureRouteState {
  capture: CaptureItemState;
  clarified: ClarifiedItemState;
  proposals: CaptureRouteProposal[];
  recommended_route_kind: string;
}

export interface CaptureCommitmentState {
  commitment_state: "committed_object" | "retained_capture" | "already_committed_object";
  resolved_proposal_id: string;
  authority_level: "auto_allowed" | "light_confirmation" | "explicit_confirmation";
  requires_confirmation: boolean;
  target_kind: string;
  target_id: string;
  destination_layer: string;
  landing_message: string;
  local_only: boolean;
  reveal_in_reflect_only: boolean;
  resolved_confirmation?: CaptureCommitConfirmation;
  // U11: schedule_block commits return both ids so the UI can reveal
  // the Plan block and the linked task without a second round-trip.
  task_id?: string | null;
  schedule_block_id?: string | null;
  // U26: echo the block's actual start/end (UTC ISO) so the UI can
  // derive the correct Plan date for the cross-surface reveal target.
  schedule_block_start_time?: string | null;
  schedule_block_end_time?: string | null;
}

export interface CaptureCommitState {
  capture: CaptureItemState;
  clarified: ClarifiedItemState;
  commitment: CaptureCommitmentState;
}

export interface CaptureCommitConfirmation {
  decision: "confirm";
  proposal_id: string;
  target_kind: "task" | "goal" | "decision" | "reference" | "capture_only" | "schedule_block";
  authority_level: "auto_allowed" | "light_confirmation" | "explicit_confirmation";
  destination_layer: string;
  owning_service: "capture_routing";
}

export interface ThoughtListResponse {
  thoughts: Thought[];
  total: number;
  categories: Record<string, number>;
}

function normalizeStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item).trim())
      .filter(Boolean);
  }

  if (typeof value !== "string") return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => String(item).trim())
        .filter(Boolean);
    }
  } catch {}

  return trimmed
    .split(",")
    .map((item) => item.replace(/^[\[\]"'\s]+|[\[\]"'\s]+$/g, "").trim())
    .filter(Boolean);
}

function normalizeThought(thought: Thought): Thought {
  return {
    ...thought,
    tags: normalizeStringArray(thought.tags),
    possible_directions: normalizeStringArray(thought.possible_directions),
    compression: thought.compression
      ? {
          ...thought.compression,
          top_themes: normalizeStringArray(thought.compression.top_themes),
        }
      : null,
  };
}

function normalizeCaptureItemState(capture: CaptureItemState): CaptureItemState {
  return {
    ...capture,
    committed_object_kind: capture.committed_object_kind ?? null,
    committed_object_id: capture.committed_object_id ?? null,
  };
}

function normalizeClarifiedItemState(clarified: ClarifiedItemState): ClarifiedItemState {
  return {
    ...clarified,
    tags: normalizeStringArray(clarified.tags),
    top_themes: normalizeStringArray(clarified.top_themes),
    possible_directions: normalizeStringArray(clarified.possible_directions),
  };
}

function normalizeDecisionOption(option: DecisionOption): DecisionOption {
  return {
    ...option,
    pros: normalizeStringArray(option.pros),
    cons: normalizeStringArray(option.cons),
  };
}

function normalizeDecision(decision: Decision): Decision {
  return {
    ...decision,
    options: Array.isArray(decision.options)
      ? decision.options.map(normalizeDecisionOption)
      : [],
    decision_factors: normalizeStringArray(decision.decision_factors),
  };
}

// ── API calls ──────────────────────────────────────────
export async function captureThought(
  text: string,
  source: string = "text"
): Promise<CaptureResponse> {
  const res = await authFetch(`${API_BASE}/thoughts`, {
    method: "POST",
    body: JSON.stringify({ text, source }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to capture thought");
  }
  const data = (await res.json()) as CaptureResponse;
  return {
    ...data,
    thought: normalizeThought(data.thought),
  };
}

export async function createCapture(
  text: string,
  source: string = "text",
  currentSurface: string = "today"
): Promise<CaptureItemState> {
  const res = await authFetch(`${API_BASE}/captures`, {
    method: "POST",
    body: JSON.stringify({ text, source, current_surface: currentSurface }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to create capture");
  }
  return normalizeCaptureItemState((await res.json()) as CaptureItemState);
}

export async function classifyCapture(
  captureId: string,
  overrideCategory?: string
): Promise<ClarifiedItemState> {
  const res = await authFetch(`${API_BASE}/captures/${captureId}/classify`, {
    method: "POST",
    body: JSON.stringify({ override_category: overrideCategory ?? null }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to classify capture");
  }
  return normalizeClarifiedItemState((await res.json()) as ClarifiedItemState);
}

export async function routeCapture(captureId: string): Promise<CaptureRouteState> {
  const res = await authFetch(`${API_BASE}/captures/${captureId}/route`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to route capture");
  }
  const data = (await res.json()) as CaptureRouteState;
  return {
    ...data,
    capture: normalizeCaptureItemState(data.capture),
    clarified: normalizeClarifiedItemState(data.clarified),
  };
}

export async function commitCapture(
  captureId: string,
  body: {
    target_kind: "task" | "goal" | "decision" | "reference" | "capture_only" | "schedule_block";
    confirmation: CaptureCommitConfirmation;
    payload?: Record<string, unknown>;
  }
): Promise<CaptureCommitState> {
  const res = await authFetch(`${API_BASE}/captures/${captureId}/commit`, {
    method: "POST",
    body: JSON.stringify({
      target_kind: body.target_kind,
      confirmation: body.confirmation,
      payload: body.payload ?? {},
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to commit capture");
  }
  const data = (await res.json()) as CaptureCommitState;
  return {
    ...data,
    capture: normalizeCaptureItemState(data.capture),
    clarified: normalizeClarifiedItemState(data.clarified),
  };
}

export async function listThoughts(
  category?: string,
  limit: number = 50,
  offset: number = 0
): Promise<ThoughtListResponse> {
  const params = new URLSearchParams();
  if (category) params.set("category", category);
  params.set("limit", String(limit));
  params.set("offset", String(offset));

  const res = await authFetch(`${API_BASE}/thoughts?${params}`);
  if (!res.ok) throw new Error("Failed to load thoughts");
  const data = (await res.json()) as ThoughtListResponse;
  return {
    ...data,
    thoughts: data.thoughts.map(normalizeThought),
  };
}

export interface ThoughtCluster {
  id: string;
  name: string;
  life_area: string | null;
  thought_count: number;
  quality_score?: number;
  quality_reason?: string;
  is_useful?: boolean;
  created_at: string;
}

export interface ClusterListResponse {
  clusters: ThoughtCluster[];
  total: number;
}

export async function listClusters(): Promise<ClusterListResponse> {
  const res = await authFetch(`${API_BASE}/thoughts/clusters`);
  if (!res.ok) throw new Error("Failed to load clusters");
  return res.json();
}

export interface ClusterMember {
  id: string;
  raw_text: string;
  category: string;
  tags: string[];
  mood: string | null;
  created_at: string | null;
}

export interface ClusterDetailResponse {
  cluster: {
    id: string;
    name: string;
    life_area: string | null;
    thought_count: number;
  };
  thoughts: ClusterMember[];
}

export async function getClusterThoughts(clusterId: string): Promise<ClusterDetailResponse> {
  const res = await authFetch(`${API_BASE}/thoughts/clusters/${clusterId}`);
  if (!res.ok) throw new Error("Failed to load cluster thoughts");
  const data = (await res.json()) as ClusterDetailResponse;
  return {
    ...data,
    thoughts: data.thoughts.map((thought) => ({
      ...thought,
      tags: normalizeStringArray(thought.tags),
    })),
  };
}

export interface ThoughtCorrection {
  category?: string;
  mood?: string;
  tags?: string[];
  extracted_action?: string;
}

export async function correctThought(
  thoughtId: string,
  correction: ThoughtCorrection
): Promise<Thought> {
  const res = await authFetch(`${API_BASE}/thoughts/${thoughtId}`, {
    method: "PATCH",
    body: JSON.stringify(correction),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to correct thought");
  }
  return normalizeThought((await res.json()) as Thought);
}

export async function archiveThought(
  thoughtId: string,
  archived: boolean = true
): Promise<Thought> {
  const res = await authFetch(`${API_BASE}/thoughts/${thoughtId}/archive`, {
    method: "POST",
    body: JSON.stringify({ archived }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to update thought");
  }
  return normalizeThought((await res.json()) as Thought);
}

export async function logThoughtFeedback(body: {
  thought_id?: string;
  event_type: string;
  surface?: string;
  recommended_action?: string;
  payload?: Record<string, unknown>;
}): Promise<void> {
  const res = await authFetch(`${API_BASE}/thoughts/feedback`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to log thought feedback");
}

// Modern screenshot capture — uploads the image, runs OCR, and walks
// the capture pipeline (create → classify → route) in one round-trip.
// Returns the same shape as POST /captures + classify + route would,
// so the popup gets full route truth (proposals, recommended_route_kind,
// captureId) and skips the legacy thought-creation path entirely.
export async function captureScreenshotV2(
  file: File,
  currentSurface: string = "today",
): Promise<CaptureRouteState> {
  const token = getAccessToken();
  const formData = new FormData();
  formData.append("image", file);
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const params = new URLSearchParams({ current_surface: currentSurface });
  const res = await fetch(`${API_BASE}/captures/screenshot?${params}`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to capture screenshot");
  }
  return res.json();
}

export async function captureScreenshot(file: File): Promise<CaptureResponse> {
  const token = getAccessToken();
  const formData = new FormData();
  formData.append("image", file);
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/thoughts/screenshot`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to capture screenshot");
  }
  const data = (await res.json()) as CaptureResponse;
  return {
    ...data,
    thought: normalizeThought(data.thought),
  };
}

// ── Big Moments (Memory Lane) ────────────────────────
export interface BigMoment {
  id: string;
  moment_type: "milestone" | "streak" | "pattern_breakthrough" | "mood_improvement" | "user_declared";
  title: string;
  description: string | null;
  stats: Record<string, number>;
  goal_id: string | null;
  celebrated: boolean;
  created_at: string;
}

export interface MomentsListResponse {
  moments: BigMoment[];
  total: number;
}

export async function listMoments(): Promise<MomentsListResponse> {
  const res = await authFetch(`${API_BASE}/analytics/moments`);
  if (!res.ok) throw new Error("Failed to load moments");
  return res.json();
}

// ── Task types ────────────────────────────────────────
export type CommitmentLevel = "must_do" | "planned" | "flexible";
export type CarryForwardOutcome = "tomorrow" | "later" | "not_now" | "made_smaller";

export interface TaskItem {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: number;
  energy_level: string;
  commitment_level: CommitmentLevel;
  estimated_minutes?: number;
  actual_minutes?: number;
  carry_forward_outcome?: CarryForwardOutcome | null;
  rollover_count?: number;
  last_rollover_at?: string | null;
  due_date?: string;
  completed_at?: string;
  parent_task_id?: string;
  goal_id?: string;
  source_agent: string;
  tags: string[];
  created_at: string;
}

export interface TaskListResponse {
  tasks: TaskItem[];
  total: number;
}

export interface EndOfDayReviewItem {
  entity_type: "task";
  id: string;
  title: string;
  description?: string | null;
  commitment_level: CommitmentLevel;
  estimated_minutes?: number | null;
  goal_id?: string | null;
  goal_title?: string | null;
  schedule_block_id?: string | null;
  scheduled_label?: string | null;
  rollover_count: number;
  carry_forward_outcome?: CarryForwardOutcome | null;
  guidance_title?: string | null;
  guidance_message?: string | null;
  recommended_outcomes?: CarryForwardOutcome[];
}

export interface EndOfDayReviewResponse {
  date: string;
  items: EndOfDayReviewItem[];
  total: number;
}

export interface CelebrationData {
  level: string; // micro | small | medium | big
  message: string;
  milestone_type?: string | null; // consistency | completion | recovery | surprise
  supporting_text?: string | null;
}

export interface TaskCompleteResponse extends TaskItem {
  celebration?: CelebrationData | null;
  next_task?: TaskItem | null;
  /** Phase 8-4.b: follow-up actions emitted by
   *  `_completion_follow_up_actions` on the backend. Typically a
   *  primary `schedule_task` (next task ready to schedule) or `plan_day`
   *  (no more tasks today) + an optional secondary `view_progress`
   *  when the completed task was linked to a goal. Max 2 entries. */
  next_actions?: NextAction[];
}

// ── Goal types ────────────────────────────────────────

// U22: goal shape — drives block template (deep_work / habit_box / exploration)
// when AI Plan schedules this goal. Mode is reversible; switching it does NOT
// erase milestones, weights, or progress.
export type GoalMode = "project" | "habit" | "direction";

// U22: per-area focus priority — cascade-enforced 1 primary + 1 secondary per
// (user, life_area). Drives time-budget share + Balance List "primary goal".
export type GoalFocusPriority = "primary" | "secondary" | "background";

export interface GoalItem {
  id: string;
  title: string;
  description?: string;
  status: string;
  category?: string;
  target_date?: string;
  progress_pct: number;
  milestones: Array<{
    title: string;
    completed: boolean;
    completed_at?: string;
    // Phase 4: optional AI-generated breakdown the user accepted.
    // When present, the milestone is effectively a parent for the
    // listed sub_steps (the actual schedulable units).
    sub_steps?: Array<{
      title: string;
      completed: boolean;
      completed_at?: string;
    }>;
  }>;
  motivation?: string;
  importance_weight?: number;
  willingness_weight?: number;
  // U22: focus_priority is cascade-enforced server-side; the Balance List
  // and life_allocation reader already consume it. Defaulted to "background"
  // when older API responses don't include it.
  focus_priority?: GoalFocusPriority;
  mode?: GoalMode;
  created_at: string;
}

export interface GoalListResponse {
  goals: GoalItem[];
  total: number;
}

// ── Mood types ────────────────────────────────────────
export interface MoodEntry {
  id: string;
  mood_score: number;
  energy_level: number;
  focus_level?: number;
  note?: string;
  tags: string[];
  source: string;
  created_at: string;
}

export interface MoodTrendResponse {
  entries: MoodEntry[];
  avg_mood: number;
  avg_energy: number;
  days: number;
}

// ── Analytics types ───────────────────────────────────
export interface QuickStats {
  tasks_completed_today: number;
  tasks_pending: number;
  thoughts_count: number;
  streak_days: number;
  current_mood?: number;
  current_energy?: number;
}

export interface WeeklyReport {
  tasks_completed: number;
  tasks_created: number;
  completion_rate: number;
  mood_trend: Array<{ date: string; mood: number; energy: number }>;
  insights: string[];
  top_productive_hours: number[];
}

// ── Brief types ───────────────────────────────────────
export interface ExecutionPatternHint {
  kind: "must_do_overload" | "smaller_steps_helping" | "repeated_restart";
  title: string;
  message: string;
  headline: string;
  next_step: string;
  recommended_commitment_level?: CommitmentLevel | null;
  recommended_commitment_label?: string | null;
  recommended_commitment_message?: string | null;
}

export interface MorningBrief {
  greeting: string;
  top_tasks: Array<{
    id: string;
    title: string;
    priority: number;
    energy_level: string;
    estimated_minutes?: number;
    due_date?: string;
  }>;
  pending_count: number;
  needs_mood_checkin: boolean;
  energy_forecast: Record<string, number>;
  insight?: string;
  // 3-4 sentence warm narrative generated by the LLM (brief.py:248-285).
  // Falls back to the statistical `insight` if the LLM call timed out or
  // returned nothing; either of the two is rendered, never both.
  ai_brief?: string | null;
  active_goals: number;
  yesterday_completed: number;
  google_connected?: boolean;
  calendar_events?: Array<{
    summary: string;
    start: string;
    end: string;
    all_day?: boolean;
  }>;
  carry_forward_summary?: {
    total: number;
    tomorrow_count: number;
    later_count: number;
    not_now_count: number;
    made_smaller_count: number;
    suggested_focus_title?: string | null;
    suggested_focus_kind?: "today" | "smaller" | "later" | null;
    execution_pattern_hint?: ExecutionPatternHint | null;
    // Up to 3 actual leftover items (made_smaller + tomorrow only) so the
    // popup can render an inline ✓ done triage row instead of bare counts.
    tasks?: Array<{
      id: string;
      title: string;
      outcome: "made_smaller" | "tomorrow" | "later" | "not_now";
      estimated_minutes?: number | null;
    }>;
  };
}

export interface ExecutionPatternSummary {
  window_days: number;
  recent_rollover_count: number;
  must_do_repeat_count: number;
  made_smaller_recent_count: number;
  repeated_restart_count: number;
  primary_hint?: ExecutionPatternHint | null;
}

export interface GoalDriftGoal {
  id: string;
  title: string;
  category?: string | null;
  progress_pct: number;
  target_date?: string | null;
  open_task_count: number;
  recent_completed_count: number;
  recent_rollover_count: number;
  total_milestones: number;
  completed_milestones: number;
  days_since_activity?: number | null;
  drift_score?: number;
  // Authoritative drifting/steady classification from the backend
  // — UI should prefer this over inspecting drift_score directly so
  // the threshold lives in one place (backend's DRIFT_SCORE_THRESHOLD).
  is_drifting?: boolean;
  recovery_note?: string | null;
  // First uncompleted milestone title for THIS goal — derived from
  // its milestone list. Used to show each ranked goal's specific next
  // step on the Schedule rail. null when the goal has no milestones
  // or all are completed.
  next_step?: string | null;
}

export interface GoalDriftSummary {
  window_days: number;
  total_goal_count: number;
  drifting_goal_count: number;
  steady_goal_count: number;
  kind: "drifting_goal" | "steady_goal" | "no_goal_signal";
  headline: string;
  message: string;
  next_step?: string | null;
  cta_label?: string | null;
  cta_destination?: "goals" | "schedule-plan" | null;
  primary_goal?: GoalDriftGoal | null;
  top_drifting_goals?: GoalDriftGoal[];
  top_steady_goals?: GoalDriftGoal[];
}

export interface QueueTaskInsight {
  id: string;
  title: string;
  priority: number;
  energy_level: string;
  estimated_minutes?: number;
  goal_id?: string | null;
  goal_title?: string | null;
  tags: string[];
  score: number;
  why_now: string;
}

export interface CalendarEvent {
  id: string;
  title: string;
  start?: string | null;
  end?: string | null;
  is_all_day: boolean;
}

export interface OpenSlot {
  start: string;
  end: string;
  duration_minutes: number;
}

export interface TimeboxBlock {
  kind: "calendar" | "task" | "buffer";
  title: string;
  start: string;
  end: string;
  task_id?: string | null;
  goal_id?: string | null;
  goal_title?: string | null;
  rationale?: string | null;
}

export interface LifestyleAnalysis {
  archetype: string;
  summary: string;
  wins: string[];
  adjustments: string[];
  focus_windows: string[];
}

export interface LifeAllocationArea {
  id: string;
  area: string;
  label: string;
  emoji: string;
  focus_level: "high" | "normal" | "low";
  focus_label: string;
  focus_score: number;
  satisfaction: number;
  goal_count: number;
  avg_progress: number;
  target_share: number;
  target_minutes_per_week: number;
  target_minutes_per_day: number;
  reason: string;
}

export interface LifeAllocationGoal {
  id: string;
  title: string;
  category?: string | null;
  allocation_mode: "protect" | "maintain" | "lighter";
  target_minutes_per_week: number;
  suggested_session_minutes: number;
  reason: string;
  importance_weight?: number;
  willingness_weight?: number;
  importance_label?: string;
  willingness_label?: string;
}

export interface LifeAllocation {
  headline: string;
  summary: string;
  weekly_focus_budget_minutes: number;
  areas: LifeAllocationArea[];
  goals: LifeAllocationGoal[];
}

function normalizeGoal(goal: GoalItem): GoalItem {
  return {
    ...goal,
    importance_weight:
      typeof goal.importance_weight === "number" ? goal.importance_weight : 0.5,
    willingness_weight:
      typeof goal.willingness_weight === "number" ? goal.willingness_weight : 0.5,
  };
}

export interface PlanningOverview {
  google_connected: boolean;
  calendar_status: "connected" | "disconnected" | "error";
  queue: QueueTaskInsight[];
  calendar_events: CalendarEvent[];
  open_slots: OpenSlot[];
  timebox: TimeboxBlock[];
  lifestyle: LifestyleAnalysis;
  suggestions: string[];
  execution_pattern_summary?: ExecutionPatternSummary | null;
  goal_drift_summary?: GoalDriftSummary | null;
  life_allocation?: LifeAllocation | null;
}

export interface GoalRoadmapStep {
  title: string;
  status: "done" | "in_progress" | "next";
  reason: string;
}

export interface GoalRoadmapVerdict {
  score: number;
  label: string;
  summary: string;
  gaps: string[];
}

export interface GoalAnalysis {
  goal_id: string;
  title: string;
  analysis: string;
  linked_tasks: number;
  linked_thoughts: number;
  roadmap: GoalRoadmapStep[];
  verdict: GoalRoadmapVerdict;
}

export interface GoalStrengthenUpdates {
  title?: string | null;
  description?: string | null;
  motivation?: string | null;
  target_date?: string | null;
}

export interface GoalStrengthenStarterTask {
  title: string;
  description: string;
  estimated_minutes: number;
  priority: number;
  energy_level: "low" | "medium" | "high";
}

export interface GoalStrengthenPlan {
  updates: GoalStrengthenUpdates;
  new_milestones: string[];
  starter_task?: GoalStrengthenStarterTask | null;
  reasoning: string;
}

export interface GoalDraft {
  title: string;
  category?: string | null;
  summary: string;
  description: string;
  motivation: string;
  analysis: string;
  target_weeks: number;
  milestones: string[];
  first_action: string;
  suggested_session_minutes: number;
}

export interface BreakdownStep {
  title: string;
  description: string;
  estimated_minutes: number;
  energy_level: "low" | "medium" | "high";
}

export interface TaskBreakdown {
  title: string;
  summary: string;
  steps: BreakdownStep[];
  first_action: string;
  suggested_session_minutes: number;
}

export interface CoachingAssist {
  pattern: string;
  reframe: string;
  next_step: string;
  support_actions: string[];
}

export interface HomeRecommendedAction {
  action_type:
    | "thought_capture"
    | "goal_draft"
    | "task_create"
    | "task_breakdown"
    | "daily_plan"
    | "coaching"
    | "decision_help"
    | "progress_analysis"
    | "lifestyle_analysis"
    | "content_capture";
  title: string;
  description: string;
  auto_apply_possible: boolean;
}

export interface HomeRelatedGoal {
  id: string;
  title: string;
  category?: string | null;
  confidence: number;
  reason: string;
  /**
   * How confident we are that this link should attach automatically.
   *
   * - `"auto"` (confidence ≥ 0.90): the matcher is confident enough to
   *   attach by default. UI shows the tag with a visible "Not this one"
   *   dismiss.
   * - `"suggested"` (0.70 ≤ confidence < 0.90): the matcher thinks this
   *   is probably right but not sure enough to silently attach. UI shows
   *   a one-tap accept/dismiss chip. User must opt in for the link to
   *   carry into the committed object.
   *
   * Captures below 0.70 return `relatedGoal === null` so `linkMode` is
   * only ever present when there IS a suggestion to show.
   */
  linkMode: "auto" | "suggested";
}

export interface HomeInterpretation {
  primary_type: HomeRecommendedAction["action_type"];
  secondary_types: HomeRecommendedAction["action_type"][];
  interpretation: string;
  why_now: string;
  recommended_actions: HomeRecommendedAction[];
  confidence: number;
  related_goal?: HomeRelatedGoal | null;
  goal_draft_preview?: GoalDraft | null;
  task_breakdown_preview?: TaskBreakdown | null;
}

export interface ThoughtLinkSuggestion {
  thought_id: string;
  thought_text: string;
  suggested_goal_id?: string | null;
  suggested_goal_title?: string | null;
  suggested_task_id?: string | null;
  suggested_task_title?: string | null;
  rationale: string;
  confidence: number;
}

export interface ThoughtBucket {
  title: string;
  count: number;
  description: string;
}

export interface ThoughtSessionCompression {
  headline: string;
  top_themes: string[];
  action_ready_count: number;
  incubating_count: number;
  emotionally_heavy_count: number;
  recurring_signals: string[];
  focus_recommendation?: string | null;
}

export interface TimeboxSuggestion {
  title: string;
  duration_minutes: number;
  source_type: "task" | "thought";
  source_id?: string | null;
  goal_title?: string | null;
  why_now: string;
}

export interface ThoughtOrganizer {
  summary: string;
  session_compression?: ThoughtSessionCompression | null;
  buckets: ThoughtBucket[];
  suggestions: ThoughtLinkSuggestion[];
  timebox_suggestions: TimeboxSuggestion[];
}

export interface DecisionContextGoal {
  id: string;
  title: string;
  category?: string | null;
  confidence: number;
  reason: string;
}

export interface DecisionContextThought {
  id: string;
  raw_text: string;
  relation: string;
}

export interface DecisionContextOption {
  name: string;
  rationale: string;
}

export interface ThoughtDecisionContext {
  thought_id: string;
  question: string;
  decision_state: "none" | "candidate" | "required" | "resolved" | "deferred";
  summary: string;
  emotional_signal?: string | null;
  focus_recommendation?: string | null;
  confidence_score: number;
  related_goals: DecisionContextGoal[];
  related_thoughts: DecisionContextThought[];
  recurring_pattern_signals: string[];
  options: DecisionContextOption[];
  tradeoffs: string[];
  recommended_path?: string | null;
}

export interface MemoryReviewItem {
  id: string;
  title: string;
  summary: string;
  kind: string;
  source: string;
  confidence?: number | null;
  updated_at?: string | null;
  tags: string[];
}

export interface MemoryReview {
  stable_preferences: MemoryReviewItem[];
  active_directions: MemoryReviewItem[];
  open_loops: MemoryReviewItem[];
  recurring_patterns: MemoryReviewItem[];
  memory_bank: MemoryReviewItem[];
  relevant_context: MemoryReviewItem[];
  refreshed_at: string;
}

// ── Chat types & API (Orchestrator) ────────────────────
export interface ChatLearningCard {
  /** Backend memory_records row id — feeds `updatePreferenceMemory(id, {importance: 0.1})`
   *  on the "That's not me" click so the visible affordance reuses the
   *  Phase 11-3.b correction pipeline exactly. */
  id: string;
  memory_type:
    | "preference"
    | "recurring_blocker"
    | "decision_rule"
    | "recurring_ambition";
  content: string;
  importance: number;
}

/**
 * Strict UAT 2026-05-24 — Chat Action Contract Layer.
 * AppActionResult is the canonical shape every Chat-triggered mutation
 * returns. Frontend reads it from `ChatApiResponse.action_result` (top
 * level) to render Undo, refresh the downstream surface, and decide
 * whether Chat is allowed to claim success.
 */
export interface AppActionUndo {
  type: string;
  entity_id: string;
  label?: string;
  extra?: Record<string, unknown>;
}

export interface AppActionResult {
  success: boolean;
  action: string;
  entity_id?: string | null;
  entity_kind?: "goal" | "task" | "schedule_block" | "decision" | "memory" | "reference" | null;
  downstream_surface?:
    | "today"
    | "plan"
    | "goals"
    | "decisions"
    | "library"
    | "settings_memory"
    | "reflect"
    | "chat"
    | null;
  refresh?: string[];
  undo_action?: AppActionUndo | null;
  user_message?: string;
  error_message?: string | null;
  idempotency_key?: string | null;
}

// ── captureALL Phase 2 — fan-out proposal types ─────────────────────────────

/** One fan-out proposal returned by POST /api/chat when the message
 *  contains multiple discrete actionable items. Matches AppProposal
 *  on the backend (backend/api/schemas.py). */
export interface AppProposal {
  id: string;
  capture_id: string;
  target_kind: string;
  label: string;
  confidence: number;
  payload: Record<string, unknown>;
  route_proposals: unknown[];
}

/** Response from POST /api/captures/fanout (Phase 1 endpoint).
 *  Also used by postCaptureFanout() below. */
export interface CaptureFanoutResponse {
  fanned_out: boolean;
  fanout_id: string | null;
  items: Array<{
    capture_id: string;
    raw_text: string;
    target_kind: string;
    confidence: number;
    route_proposals: unknown[];
    status: string;
  }>;
  truncated: boolean;
}

export interface ChatApiResponse {
  response_text: string;
  response_type: string;
  data: Record<string, unknown>;
  /** Strict UAT 2026-05-24: top-level canonical action contract.
   *  When present and `success` is true, the chat reply represents a
   *  durable mutation and the frontend MUST refresh the listed
   *  downstream surfaces + render Undo if `undo_action` is set. */
  action_result?: AppActionResult | null;
  intent: string;
  agent_name?: string;
  agent_color?: string;
  memory_refs?: Array<{ type: string; summary: string }>;
  suggestions?: string[];
  next_actions?: NextAction[];
  response_mode?: string;
  schedule_blocks?: ChatPlanBlock[];
  /** Phase 11-5: surfaced when the inline preference extractor wrote a
   *  high-importance new row during this assistant turn. */
  learning_card?: ChatLearningCard | null;
  /** captureALL Phase 2: fan-out proposals when the message contained
   *  multiple discrete actionable items. Empty array on normal replies. */
  proposals?: AppProposal[];
  /** captureALL Phase 2: "inline" for 1-4 items, "modal" for 5+. */
  proposal_mode?: "inline" | "modal";
}

export interface WorkflowDebugMergeEvent {
  type: string;
  node_id: string;
  key: string;
  policy: string;
  workflow: string;
}

export interface WorkflowDebugSummary {
  route_type: string;
  route_reason: string;
  intents: string[];
  primary_intent: string;
  routed_to: string;
  workflow_name: string;
  classifier_confidence: number | null;
  classifier_candidates: Array<{ intent: string; confidence: number }>;
  screen_context: string;
  screen_bias_applied: boolean;
  screen_bias_changed_primary: boolean;
  pre_bias_primary_intent: string;
  node_statuses: Record<string, string>;
  merge_events: WorkflowDebugMergeEvent[];
  debug_event_count: number;
}

export interface NextAction {
  id: string;
  type: string;
  label: string;
  style: "primary" | "secondary" | "ghost";
  requires_confirmation: boolean;
  payload: Record<string, unknown>;
}

export interface ChatPlanBlock {
  title: string;
  start_time: string;
  end_time: string;
  duration_min: number;
  goal_title: string | null;
  rationale: string | null;
  kind: string;
  color: string | null;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  response_type?: string;
  intent?: string;
  data?: Record<string, unknown>;
  agent_name?: string;
  agent_color?: string;
  memory_refs?: Array<{ type: string; summary: string }>;
  suggestions?: string[];
  next_actions?: NextAction[];
  response_mode?: string;
  schedule_blocks?: ChatPlanBlock[];
  /** Phase 11-5: visible "I just noticed: X" affordance for the most
   *  recent assistant turn that produced a new high-importance memory. */
  learning_card?: ChatLearningCard | null;
  /** U24 — Set on the action_result bubble when the action commit
   *  returned a reveal target. Renders an inline "Open in {Surface}"
   *  banner so the user can land on what just got created. */
  reveal?: ActionReveal | null;
  timestamp: number;
}

export async function sendChat(
  message: string,
  screenContext?: string,
  focusedGoalId?: string | null,
): Promise<ChatApiResponse> {
  const isLocalDevHost =
    typeof window !== "undefined" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname);
  const includeWorkflowDebug =
    typeof window !== "undefined" &&
    (process.env.NODE_ENV !== "production" || isLocalDevHost);
  // Pass the browser's tz offset so the backend can compute the user's
  // local "today" boundary for schedule preview. Date.getTimezoneOffset()
  // returns minutes BEHIND UTC (e.g., 480 for UTC-8); negate it so the
  // backend gets the conventional signed offset (-480 for UTC-8).
  const tzOffsetMinutes =
    typeof Date !== "undefined" ? -new Date().getTimezoneOffset() : 0;
  // WIP-1 § 8-7 U5: only forward focused_goal_id when chat is operating on
  // the Goals surface. Other surfaces never have a meaningful "focused goal"
  // signal, so leaking the id there would mis-ground unrelated conversations.
  const includeFocusedGoal =
    typeof focusedGoalId === "string" &&
    focusedGoalId.length > 0 &&
    screenContext === "goals";
  const body: Record<string, unknown> = {
    message,
    screen_context: screenContext,
    tz_offset_minutes: tzOffsetMinutes,
    include_workflow_debug: includeWorkflowDebug,
  };
  if (includeFocusedGoal) {
    body.focused_goal_id = focusedGoalId;
  }
  const res = await authFetch(`${API_BASE}/chat`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Something went wrong");
  }
  return res.json();
}

// ── captureALL Phase 2 — fan-out API helpers ──────────────────────────────

/** POST /api/captures/fanout — explicit fan-out trigger (Phase 1 endpoint).
 *  Phase 2 chat auto-triggers fan-out via POST /api/chat; this function
 *  is provided for direct usage (e.g. HomeScreen quick-capture flow). */
export async function postCaptureFanout(
  text: string
): Promise<CaptureFanoutResponse> {
  const res = await authFetch(`${API_BASE}/captures/fanout`, {
    method: "POST",
    body: JSON.stringify({ raw_text: text }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { detail?: string }).detail || "Fan-out failed");
  }
  return res.json() as Promise<CaptureFanoutResponse>;
}

/** POST /api/captures/{id}/commit — commit a drafted capture to a real object.
 *  Re-uses the existing commitCapture() backend contract; this alias exists
 *  so fan-out consumers have a purpose-named helper. */
export async function commitCaptureDraft(
  captureId: string,
  payload?: Record<string, unknown>
): Promise<unknown> {
  const res = await authFetch(`${API_BASE}/captures/${captureId}/commit`, {
    method: "POST",
    body: JSON.stringify({ payload: payload ?? {} }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { detail?: string }).detail || "Commit failed");
  }
  return res.json();
}

/** Dismiss all drafted captures in a fan-out group.
 *
 * Phase 4: backend endpoint TBD — the dismiss endpoint
 * (`DELETE /api/captures/fanout/{fanout_id}`) has not been built yet.
 * Returning a resolved Promise so callers can already wire this in
 * their UI; the real implementation will swap this body out in Phase 4.
 *
 * Note: throwing here would cause fan-out dismiss buttons to error on
 * click before Phase 4 lands, which is worse UX than a silent no-op.
 */
export async function dismissCaptureFanout(
  _fanoutId: string
): Promise<unknown> {
  // Phase 4: backend endpoint TBD
  return Promise.resolve({ dismissed: true, phase: "placeholder" });
}

// ── Action Engine ─────────────────────────────────────
export interface ActionReveal {
  /** UI surface that owns the durable object the action just created/updated. */
  surface: "today" | "plan" | "reflect" | "goals" | "library" | "decisions";
  /** Real id of the row, NOT the action id. Use this to focus/scroll to it. */
  id: string;
  /** Human-readable label for the visible cue (toast / banner / focus ring). */
  label: string;
  /** Optional ISO date (YYYY-MM-DD) for date-keyed surfaces (Plan). Surfaces
   *  that display a single-day slice MUST switch to this date before the
   *  highlight pipeline runs, otherwise `data-reveal-id` won't match — the
   *  block isn't in the DOM yet for the wrong day. Reflect "Schedule for
   *  {weekday}" populates this; chat reveals without an explicit date leave
   *  it undefined (today is the implicit default for those). */
  date?: string;
}

export interface ActionExecuteResult {
  // Phase 8-2 hardening (2026-05-24): `expired` added so the union
  // matches the backend's terminal-state DAG. Both `expired` and
  // `failed` should be rendered as no-op terminal messages, not
  // retried — the backend has already gated this server-side.
  status: "success" | "dismissed" | "already_completed" | "failed" | "expired";
  result: Record<string, unknown>;
  /** Where the user can find what just landed. Only present when an action
   *  produced or modified a durable object (UAT 2026-05-06 fix #4). */
  reveal?: ActionReveal | null;
  follow_up?: {
    message: string;
    intent: string;
    response_mode: string;
    next_actions: NextAction[];
  };
}

export async function executeAction(actionId: string): Promise<ActionExecuteResult> {
  // UAT 2026-05-24: thread browser tz so `plan_day` anchors the day
  // window to the user's local timezone (otherwise backend defaults to
  // tz_offset=0 and blocks land at wrong hours). JS convention is
  // -getTimezoneOffset() so positive for east-of-UTC.
  const tz_offset_minutes = -new Date().getTimezoneOffset();
  const res = await authFetch(`${API_BASE}/actions/execute`, {
    method: "POST",
    body: JSON.stringify({ action_id: actionId, tz_offset_minutes }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Action failed");
  }
  return res.json();
}

/** Phase 8-4.a: TodayScreen empty-state one-click "Plan my day" entry point.
 *  Server-side persists an `ActionSuggestion` row (source_agent="empty_state",
 *  action_type="plan_day") for funnel tracking, runs the standard
 *  `_exec_plan_day` pipeline, and returns the typed `ActionExecuteResult`.
 *  The caller uses `result.reveal.surface === "plan"` to navigate.
 */
export async function triggerEmptyStatePlanDay(): Promise<ActionExecuteResult> {
  const res = await authFetch(`${API_BASE}/plan/empty-state-trigger`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Could not plan your day");
  }
  return res.json();
}

// ── Health check ───────────────────────────────────────
export async function checkHealth(): Promise<{ status: string }> {
  const res = await fetch("/health");
  if (!res.ok) throw new Error("Backend not reachable");
  return res.json();
}

// ── Tasks API ─────────────────────────────────────────
export async function listTasks(
  status?: string,
  limit: number = 50,
  options?: { todayOnly?: boolean }
): Promise<TaskListResponse> {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  params.set("limit", String(limit));
  if (options?.todayOnly) params.set("today_only", "true");
  const res = await authFetch(`${API_BASE}/tasks?${params}`);
  if (!res.ok) throw new Error("Failed to load tasks");
  return res.json();
}

export async function completeTask(
  taskId: string,
  energyAfter?: number,
  actualMinutes?: number,
): Promise<TaskCompleteResponse> {
  const searchParams = new URLSearchParams();
  if (actualMinutes != null) searchParams.set("actual_minutes", String(actualMinutes));
  if (energyAfter != null) searchParams.set("energy_after", String(energyAfter));
  const params = searchParams.toString() ? `?${searchParams}` : "";
  const res = await authFetch(`${API_BASE}/tasks/${taskId}/complete${params}`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to complete task");
  }
  return res.json();
}

export async function skipTask(taskId: string): Promise<TaskItem> {
  const res = await authFetch(`${API_BASE}/tasks/${taskId}/skip`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to skip task");
  return res.json();
}

export async function undoTaskCompletion(taskId: string): Promise<TaskItem> {
  const res = await authFetch(`${API_BASE}/tasks/${taskId}/undo-complete`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to undo task completion");
  }
  return res.json();
}

// Reverses a chat-driven autonomous action (goal_created / decision_created /
// mood_logged / etc.). The payload mirrors `data.undo_action` from the chat
// response: `{ type, entity_id, label }`. WIP-1 § 8-7 vertical slice.
export interface ChatUndoRequest {
  action_type: string;
  entity_id: string;
  extra?: Record<string, unknown>;
}

export async function undoChatAction(req: ChatUndoRequest): Promise<{ status?: string }> {
  const res = await authFetch(`${API_BASE}/chat/undo`, {
    method: "POST",
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to undo chat action");
  }
  return res.json().catch(() => ({}));
}

export async function getEndOfDayReview(date?: string): Promise<EndOfDayReviewResponse> {
  const params = date ? `?date=${encodeURIComponent(date)}` : "";
  const res = await authFetch(`${API_BASE}/planning/end-of-day-review${params}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to load end-of-day review");
  }
  return res.json();
}

export async function suggestSmallerStep(
  taskId: string,
): Promise<{ suggested_title: string | null }> {
  const res = await authFetch(`${API_BASE}/tasks/${taskId}/suggest-smaller`, {
    method: "POST",
  });
  if (!res.ok) {
    return { suggested_title: null };
  }
  return res.json();
}

export async function carryForwardTask(
  taskId: string,
  body: {
    outcome: CarryForwardOutcome;
    target_date?: string;
    smaller_title?: string;
  }
): Promise<TaskItem> {
  const res = await authFetch(`${API_BASE}/tasks/${taskId}/carry-forward`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to update carry-forward");
  }
  return res.json();
}

export async function updateTask(
  taskId: string,
  body: Partial<{
    title: string;
    description: string;
    status: "pending" | "in_progress" | "completed" | "skipped" | "deferred";
    priority: number;
    energy_level: "low" | "medium" | "high";
    commitment_level: "must_do" | "planned" | "flexible";
    estimated_minutes: number;
    actual_minutes: number;
    due_date: string;
  }>
): Promise<TaskItem> {
  const res = await authFetch(`${API_BASE}/tasks/${taskId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to update task");
  }
  return res.json();
}

// ── Goals API ─────────────────────────────────────────
export async function listGoals(
  status?: string
): Promise<GoalListResponse> {
  const params = status ? `?status=${status}` : "";
  const res = await authFetch(`${API_BASE}/goals${params}`);
  if (!res.ok) throw new Error("Failed to load goals");
  const data = (await res.json()) as GoalListResponse;
  return {
    ...data,
    goals: data.goals.map(normalizeGoal),
  };
}

export async function createGoal(body: {
  title: string;
  description?: string;
  category?: string;
  target_date?: string;
  motivation?: string;
  importance_weight?: number;
  willingness_weight?: number;
}): Promise<GoalItem> {
  const res = await authFetch(`${API_BASE}/goals`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to create goal");
  return normalizeGoal((await res.json()) as GoalItem);
}

export async function updateGoal(
  goalId: string,
  body: Partial<{
    title: string;
    description: string;
    status: string;
    category: string;
    target_date: string;
    progress_pct: number;
    motivation: string;
    importance_weight: number;
    willingness_weight: number;
    // U22
    mode: GoalMode;
    focus_priority: GoalFocusPriority;
  }>
): Promise<GoalItem> {
  const res = await authFetch(`${API_BASE}/goals/${goalId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to update goal");
  return normalizeGoal((await res.json()) as GoalItem);
}

export async function addGoalMilestone(goalId: string, title: string): Promise<GoalItem> {
  const res = await authFetch(`${API_BASE}/goals/${goalId}/milestone`, {
    method: "POST",
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error("Failed to add milestone");
  return normalizeGoal((await res.json()) as GoalItem);
}

export interface MilestoneSubStepInput {
  title: string;
  completed: boolean;
  completed_at?: string | null;
}

export interface MilestoneInput {
  title: string;
  completed: boolean;
  completed_at?: string | null;
  // Optional nested breakdown (Phase 4 AI judge result the user
  // accepted). Sub-steps are the actual schedulable units; the
  // parent milestone rolls up to "completed" when all sub_steps
  // are completed.
  sub_steps?: MilestoneSubStepInput[];
}

// Phase 4: AI judge endpoint. Asks the backend to evaluate whether
// a single goal "next step" is suitable for one timebox or needs
// to be broken down. No DB writes — pure inference.
export interface JudgeStepResult {
  verdict: "schedulable" | "too_big";
  estimated_minutes?: number | null;
  reason: string;
  sub_steps: string[];
}

export async function judgeGoalStep(
  goalId: string,
  body: {
    step_title: string;
    focus_minutes?: number;
    goal_title?: string;
    goal_description?: string;
  },
): Promise<JudgeStepResult> {
  const res = await authFetch(`${API_BASE}/goals/${goalId}/judge-step`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to judge step");
  }
  return (await res.json()) as JudgeStepResult;
}

// Wholesale replace this goal's milestone list. Used by the editable
// milestone UI: send the full ordered array the user wants saved,
// server replaces and recomputes progress in one shot.
export async function replaceGoalMilestones(
  goalId: string,
  milestones: MilestoneInput[],
): Promise<GoalItem> {
  const res = await authFetch(`${API_BASE}/goals/${goalId}/milestones`, {
    method: "PUT",
    body: JSON.stringify({ milestones }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to update milestones");
  }
  return normalizeGoal((await res.json()) as GoalItem);
}

// Soft archive — keeps the goal row, just flips status so it's
// hidden from default filters but recoverable via PATCH back to
// "active". Cleaner than DELETE for "I don't want this on my list
// right now but might come back to it".
export async function archiveGoal(goalId: string): Promise<GoalItem> {
  return updateGoal(goalId, { status: "abandoned" });
}

// Restore an archived goal back to a chosen non-archived state.
// "active" means the user wants to keep working on it; "completed"
// means the goal was finished before being archived and they just
// want it visible in the Completed tab again. Default is "active"
// since that's the most common path. Same shape as archive,
// opposite direction.
export async function restoreGoal(
  goalId: string,
  targetStatus: "active" | "completed" = "active",
): Promise<GoalItem> {
  return updateGoal(goalId, { status: targetStatus });
}

// Hard delete — true removal. Linked tasks/blocks survive via
// ON DELETE SET NULL on the FK. Use for cleanup (duplicates,
// experimental goals).
export async function deleteGoal(goalId: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/goals/${goalId}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to delete goal");
  }
}

export async function completeMilestone(goalId: string, title: string): Promise<GoalItem> {
  const params = new URLSearchParams({ title });
  const res = await authFetch(`${API_BASE}/goals/${goalId}/milestone/complete?${params}`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to complete milestone");
  return normalizeGoal((await res.json()) as GoalItem);
}

// Phase 4: complete a single sub_step under a milestone. The
// backend auto-rolls-up the parent milestone when every sub_step is
// done, so callers don't need to track that separately.
export async function completeMilestoneSubStep(
  goalId: string,
  parentTitle: string,
  index: number,
): Promise<GoalItem> {
  const params = new URLSearchParams({
    parent_title: parentTitle,
    index: String(index),
  });
  const res = await authFetch(
    `${API_BASE}/goals/${goalId}/milestone/sub_step/complete?${params}`,
    { method: "POST" },
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to complete sub-step");
  }
  return normalizeGoal((await res.json()) as GoalItem);
}

export async function undoMilestone(goalId: string, title: string): Promise<GoalItem> {
  const params = new URLSearchParams({ title });
  const res = await authFetch(`${API_BASE}/goals/${goalId}/milestone/uncomplete?${params}`, {
    method: "POST",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to undo milestone");
  }
  return normalizeGoal((await res.json()) as GoalItem);
}

export async function createTask(body: {
  title: string;
  description?: string;
  priority?: number;
  energy_level?: "low" | "medium" | "high";
  commitment_level?: CommitmentLevel;
  estimated_minutes?: number;
  due_date?: string;
  parent_task_id?: string;
  goal_id?: string;
  tags?: string[];
}): Promise<TaskItem> {
  const res = await authFetch(`${API_BASE}/tasks`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to create task");
  return res.json();
}

// ── Mood API ──────────────────────────────────────────
export async function logMood(body: {
  mood_score: number;
  energy_level: number;
  focus_level?: number;
  note?: string;
}): Promise<MoodEntry> {
  const res = await authFetch(`${API_BASE}/mood`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to log mood");
  return res.json();
}

export async function getMoodTrend(days: number = 7): Promise<MoodTrendResponse> {
  const res = await authFetch(`${API_BASE}/mood/trend?days=${days}`);
  if (!res.ok) throw new Error("Failed to load mood trend");
  return res.json();
}

// ── Analytics API ─────────────────────────────────────
export async function getQuickStats(): Promise<QuickStats> {
  const res = await authFetch(`${API_BASE}/analytics/stats`);
  if (!res.ok) throw new Error("Failed to load stats");
  return res.json();
}

export async function getWeeklyReport(): Promise<WeeklyReport> {
  const res = await authFetch(`${API_BASE}/analytics/weekly`);
  if (!res.ok) throw new Error("Failed to load weekly report");
  return res.json();
}

// ── Milestones types & API ───────────────────────────
export interface MilestoneItem {
  id: string;
  type: string;  // consistency | completion | recovery | surprise
  message: string;
  goal_title: string | null;
  celebrated_at: string | null;
}

export interface MilestoneListResponse {
  milestones: MilestoneItem[];
  total: number;
}

export async function getMilestones(limit: number = 10): Promise<MilestoneListResponse> {
  const res = await authFetch(`${API_BASE}/analytics/milestones?limit=${limit}`);
  if (!res.ok) throw new Error("Failed to load milestones");
  return res.json();
}

// ── Pattern Insights API ──────────────────────────────
export interface PatternInsights {
  insights: string[];
  productive_hours: {
    peak_hours: number[];
    low_hours: number[];
    completions_by_hour: Record<string, number>;
  } | null;
  completion_patterns: {
    completion_rate: number;
    avg_tasks_per_day: number;
    preferred_duration: string | null;
    current_streak: number;
    best_streak: number;
  } | null;
  procrastination_triggers: Array<{
    trigger: string;
    skip_rate: number;
  }>;
  energy_patterns: {
    avg_energy_when_completing: number | null;
    avg_energy_when_skipping: number | null;
  } | null;
  goal_consistency: {
    most_consistent_goal: string | null;
    neglected_goals: string[];
  } | null;
  struggle_patterns: {
    periods: Array<{ start: string; end: string; days: number }>;
  } | null;
  recovery_patterns: {
    avg_recovery_days: number | null;
    recovery_rate: number | null;
  } | null;
}

export async function getPatternInsights(): Promise<PatternInsights> {
  const res = await authFetch(`${API_BASE}/analytics/patterns`);
  if (!res.ok) throw new Error("Failed to load patterns");
  return res.json();
}

// ── Daily Analysis API ────────────────────────────────
export interface DailyAnalysis {
  tasks_completed: number;
  tasks_skipped: number;
  content_consumed: number;
  goals_progressed: string[];
  mood_signal: string | null;
  ai_summary: string;
  milestones?: string[];
  cached: boolean;
}

export async function getDailyAnalysis(): Promise<DailyAnalysis> {
  const res = await authFetch(`${API_BASE}/analytics/daily-analysis`);
  if (!res.ok) throw new Error("Failed to load daily analysis");
  return res.json();
}

// ── Brief API ─────────────────────────────────────────
export async function getMorningBrief(
  options?: { phase?: "morning" | "evening" },
): Promise<MorningBrief> {
  const phase = options?.phase ?? "morning";
  const url = `${API_BASE}/brief/morning${phase === "evening" ? "?phase=evening" : ""}`;
  const res = await authFetch(url);
  if (!res.ok) throw new Error("Failed to load morning brief");
  return res.json();
}

export async function getExecutionPatternSummary(): Promise<ExecutionPatternSummary> {
  const res = await authFetch(`${API_BASE}/planning/execution-patterns`);
  if (!res.ok) throw new Error("Failed to load execution patterns");
  return res.json();
}

export async function getGoalDriftSummary(): Promise<GoalDriftSummary> {
  const res = await authFetch(`${API_BASE}/planning/goal-drift-summary`);
  if (!res.ok) throw new Error("Failed to load goal drift summary");
  return res.json();
}

// U22 — Layer 1 week-shape response
export interface WeeklyGoalShape {
  goal_id: string;
  goal_title: string;
  template_kind: "deep_work" | "habit_box" | "exploration";
  target_minutes: number;
  session_count: number;
  preferred_session_minutes: number;
  preferred_window: "am_peak" | "anchor" | "low_energy_slot";
  protection_tier: "protect" | "maintain" | "lighter";
  weekday_preference: number[] | null;
  reason: string;
}

export interface WeekShape {
  shapes: WeeklyGoalShape[];
}

export async function getWeekShape(): Promise<WeekShape> {
  const res = await authFetch(`${API_BASE}/planning/week-shape`);
  if (!res.ok) throw new Error("Failed to load weekly shape");
  return res.json();
}

export interface PlacedBlock {
  goal_id: string;
  start_at: string;
  end_at: string;
  template_kind: "deep_work" | "habit_box" | "exploration";
  protection_tier: "protect" | "maintain" | "lighter";
  title: string;
  reason: string;
}

export interface PlaceDayResult {
  date: string;
  placed_blocks: PlacedBlock[];
  skipped: { goal_id: string; reason: string }[];
}

export async function placeDay(target: string): Promise<PlaceDayResult> {
  const res = await authFetch(`${API_BASE}/planning/place-day`, {
    method: "POST",
    body: JSON.stringify({ date: target }),
  });
  if (!res.ok) throw new Error("Failed to commit day plan");
  return res.json();
}

export interface EveningRecap {
  completed_today: { id: string; title: string; actual_minutes: number | null }[];
  completed_count: number;
  missed_tasks: { id: string; title: string; priority: number }[];
  missed_count: number;
  mood_today: { mood: number; energy: number } | null;
  summary: string;
}

export async function getEveningBrief(): Promise<EveningRecap> {
  const res = await authFetch(`${API_BASE}/brief/evening`);
  if (!res.ok) throw new Error("Failed to load evening brief");
  return res.json();
}

// ── Planning API ──────────────────────────────────────
export async function getPlanningOverview(date?: string): Promise<PlanningOverview> {
  // Pass the user's tz so the backend can compute calm-opening windows
  // against their local wake_time / wind_down rather than UTC 8-9 PM.
  // Signed JS convention (positive minutes for west-of-UTC).
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  search.set("tz_offset_minutes", String(new Date().getTimezoneOffset()));
  const res = await authFetch(`${API_BASE}/planning/overview?${search.toString()}`);
  if (!res.ok) throw new Error("Failed to load planning overview");
  return res.json();
}

export async function getGoalAnalysis(goalId: string): Promise<GoalAnalysis> {
  const res = await authFetch(`${API_BASE}/planning/goals/${goalId}/analysis`);
  if (!res.ok) throw new Error("Failed to load goal analysis");
  return res.json();
}

export async function getGoalStrengthenPlan(goalId: string): Promise<GoalStrengthenPlan> {
  const res = await authFetch(`${API_BASE}/planning/goals/${goalId}/strengthen`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to strengthen goal");
  return res.json();
}

export async function getGoalDraft(text: string): Promise<GoalDraft> {
  const res = await authFetch(`${API_BASE}/planning/goals/draft`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error("Failed to draft goal");
  return res.json();
}

// ── Retroactive Milestone Scan ───────────────────────
export interface RetroactiveMatch {
  milestone_title: string;
  items: { type: "task" | "thought"; id: string; title: string; completed_at: string | null }[];
}

export interface RetroactiveScanResponse {
  matches: RetroactiveMatch[];
  total_matches: number;
}

export async function retroactiveScan(goalId: string): Promise<RetroactiveScanResponse> {
  const res = await authFetch(`${API_BASE}/goals/${goalId}/retroactive-scan`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to scan for retroactive progress");
  return res.json();
}

export async function retroactiveApply(goalId: string, confirmed: string[]): Promise<{ updated: number; progress_pct: number }> {
  const res = await authFetch(`${API_BASE}/goals/${goalId}/retroactive-apply`, {
    method: "POST",
    body: JSON.stringify({ confirmed }),
  });
  if (!res.ok) throw new Error("Failed to apply retroactive progress");
  return res.json();
}

export async function getTaskBreakdown(text: string): Promise<TaskBreakdown> {
  const res = await authFetch(`${API_BASE}/planning/tasks/breakdown`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error("Failed to break down task");
  return res.json();
}

export async function getCoachingAssist(text: string): Promise<CoachingAssist> {
  const res = await authFetch(`${API_BASE}/planning/coaching/assist`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error("Failed to load coaching assist");
  return res.json();
}

export async function interpretHomeInput(text: string): Promise<HomeInterpretation> {
  const res = await authFetch(`${API_BASE}/planning/home/interpret`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error("Failed to interpret input");
  return res.json();
}

export async function getThoughtOrganizer(): Promise<ThoughtOrganizer> {
  const res = await authFetch(`${API_BASE}/planning/thoughts/organize`);
  if (!res.ok) throw new Error("Failed to load thought organizer");
  const data = (await res.json()) as ThoughtOrganizer;
  return {
    ...data,
    session_compression: data.session_compression
      ? {
          ...data.session_compression,
          top_themes: normalizeStringArray(data.session_compression.top_themes),
          recurring_signals: normalizeStringArray(data.session_compression.recurring_signals),
        }
      : null,
  };
}

export async function getThoughtDecisionContext(thoughtId: string): Promise<ThoughtDecisionContext> {
  const res = await authFetch(`${API_BASE}/planning/thoughts/${thoughtId}/decision-context`);
  if (!res.ok) throw new Error("Failed to load decision context");
  const data = (await res.json()) as ThoughtDecisionContext;
  return {
    ...data,
    recurring_pattern_signals: normalizeStringArray(data.recurring_pattern_signals),
    options: Array.isArray(data.options)
      ? data.options.map((option) => ({
          ...option,
          name: String(option.name || "").trim(),
          rationale: String(option.rationale || "").trim(),
        }))
      : [],
  };
}

function normalizeMemoryReviewItem(item: MemoryReviewItem): MemoryReviewItem {
  return {
    ...item,
    tags: normalizeStringArray(item.tags),
  };
}

export async function getMemoryOverview(query?: string): Promise<MemoryReview> {
  const params = new URLSearchParams();
  if (query?.trim()) params.set("query", query.trim());
  const queryString = params.toString();
  const res = await authFetch(`${API_BASE}/planning/memory/overview${queryString ? `?${queryString}` : ""}`);
  if (!res.ok) throw new Error("Failed to load memory overview");
  const data = (await res.json()) as MemoryReview;
  return {
    ...data,
    stable_preferences: (data.stable_preferences || []).map(normalizeMemoryReviewItem),
    active_directions: (data.active_directions || []).map(normalizeMemoryReviewItem),
    open_loops: (data.open_loops || []).map(normalizeMemoryReviewItem),
    recurring_patterns: (data.recurring_patterns || []).map(normalizeMemoryReviewItem),
    memory_bank: (data.memory_bank || []).map(normalizeMemoryReviewItem),
    relevant_context: (data.relevant_context || []).map(normalizeMemoryReviewItem),
  };
}

// ── Roadmap Resources types ─────────────────────────
export interface RoadmapResource {
  title: string;
  url: string;
  resource_type: string;
  description: string;
}

export interface RoadmapResourcesResult {
  step_title: string;
  resources: RoadmapResource[];
}

export async function getRoadmapResources(
  goalId: string,
  stepTitle: string,
  stepReason: string
): Promise<RoadmapResourcesResult> {
  const res = await authFetch(
    `${API_BASE}/planning/goals/${goalId}/roadmap-resources`,
    {
      method: "POST",
      body: JSON.stringify({ step_title: stepTitle, step_reason: stepReason }),
    }
  );
  if (!res.ok) throw new Error("Failed to load roadmap resources");
  return res.json();
}

// ── Content Queue types ──────────────────────────────
export interface ContentQueueItem {
  id: string;
  title: string;
  // Full body for note-style references and snippet for link
  // references — populated by the modern reference commit and any
  // direct POST /api/queue caller. Older queue items remain null.
  description?: string | null;
  url: string | null;
  content_type: string | null;
  duration_min: number | null;
  goal_id: string | null;
  goal_title: string | null;
  goal_category: string | null;
  goal_match_pct: number | null;
  priority_score: number;
  status: string;
  postpone_count: number;
  priority_reason: string | null;
  saved_at: string | null;
}

export interface ContentQueueListResponse {
  items: ContentQueueItem[];
  total: number;
}

export interface ContentQueuePreview {
  title: string;
  content_type: string | null;
  duration_min?: number | null;
  provider?: string | null;
}

// ── Content Queue API ────────────────────────────────
export async function getContentQueue(
  status: string = "queued",
  contentType?: string,
  limit: number = 20
): Promise<ContentQueueListResponse> {
  const params = new URLSearchParams();
  params.set("status", status);
  if (contentType) params.set("content_type", contentType);
  params.set("limit", String(limit));
  const res = await authFetch(`${API_BASE}/queue?${params}`);
  if (!res.ok) throw new Error("Failed to load content queue");
  return res.json();
}

export async function addToContentQueue(body: {
  title: string;
  description?: string;
  url?: string;
  content_type?: string;
  duration_min?: number;
  goal_id?: string;
}): Promise<ContentQueueItem> {
  const res = await authFetch(`${API_BASE}/queue`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to add to queue");
  return res.json();
}

export async function previewContentCapture(text: string): Promise<ContentQueuePreview> {
  const res = await authFetch(`${API_BASE}/queue/preview`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  if (!res.ok) throw new Error("Failed to preview content capture");
  return res.json();
}

export async function updateContentQueueItem(
  itemId: string,
  body: {
    status?: string;
    title?: string;
    description?: string;
    url?: string;
    content_type?: string;
    duration_min?: number;
    goal_id?: string;
  }
): Promise<ContentQueueItem> {
  const res = await authFetch(`${API_BASE}/queue/${itemId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to update queue item");
  return res.json();
}

export async function postponeContentQueueItem(
  itemId: string
): Promise<ContentQueueItem> {
  const res = await authFetch(`${API_BASE}/queue/${itemId}/postpone`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to postpone queue item");
  return res.json();
}

// ── Content Summarize ────────────────────────────────
export interface ContentSummary {
  key_points: string[];
  goal_relevance: string;
  suggested_actions: string[];
  time_estimate: string;
}

export interface SummarizeResponse {
  item_id: string;
  title: string;
  summary: ContentSummary;
  source: "ai" | "template";
}

export async function summarizeQueueItem(itemId: string): Promise<SummarizeResponse> {
  const res = await authFetch(`${API_BASE}/queue/${itemId}/summarize`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to summarize content");
  return res.json();
}

// ── Schedule types ────────────────────────────────────
export interface ScheduleBlock {
  id: string;
  kind: "task" | "calendar" | "buffer" | "break" | "custom";
  title: string;
  description?: string | null;
  start_time: string;
  end_time: string;
  commitment_level: CommitmentLevel;
  task_id?: string | null;
  goal_id?: string | null;
  goal_title?: string | null;
  goal_category?: string | null;
  life_area?: string | null;
  rationale?: string | null;
  color: string;
  carry_forward_outcome?: CarryForwardOutcome | null;
  rollover_count?: number;
  last_rollover_at?: string | null;
  priority?: number | null;
  due_date?: string | null;
  duration_days?: number | null;
  is_completed: boolean;
  is_ai_generated: boolean;
  schedule_date: string;
  created_at: string;
  block_metadata?: Record<string, unknown>;
}

export interface ScheduleBlockCompleteResult extends ScheduleBlock {
  task_completed: boolean;
  goal_updated: boolean;
  goal_progress_pct: number | null;
  milestones_auto_completed: number;
}

export interface ScheduleResponse {
  blocks: ScheduleBlock[];
  date: string;
  total: number;
}

export interface GoogleScheduleSyncResult {
  date: string;
  synced_count: number;
  created_count: number;
  updated_count: number;
  detail: string;
}

// ── Schedule API ──────────────────────────────────────
export async function getSchedule(date?: string): Promise<ScheduleResponse> {
  // Pass the browser's timezone offset (JS convention: positive minutes
  // for west-of-UTC) so the backend can return the user's local-day
  // window. Without this, evening blocks in negative-offset zones cross
  // UTC midnight and disappear from the response.
  const tzOffset = new Date().getTimezoneOffset();
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  search.set("tz_offset_minutes", String(tzOffset));
  const res = await authFetch(`${API_BASE}/schedule?${search.toString()}`);
  if (!res.ok) throw new Error("Failed to load schedule");
  return res.json();
}

export async function createScheduleBlock(body: {
  kind: string;
  title: string;
  start_time: string;
  end_time: string;
  description?: string;
  commitment_level?: CommitmentLevel;
  task_id?: string;
  goal_id?: string;
  goal_title?: string;
  life_area?: string;
  rationale?: string;
  color?: string;
  priority?: number;
  due_date?: string;
  duration_days?: number;
}): Promise<ScheduleBlock> {
  const res = await authFetch(`${API_BASE}/schedule`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to create schedule block");
  return res.json();
}

export async function updateScheduleBlock(
  blockId: string,
  body: Partial<{
    title: string;
    description: string;
    start_time: string;
    end_time: string;
    commitment_level: CommitmentLevel;
    color: string;
    kind: string;
    life_area: string | null;
    priority: number | null;
    due_date: string | null;
    duration_days: number | null;
  }>
): Promise<ScheduleBlock> {
  const res = await authFetch(`${API_BASE}/schedule/${blockId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to update schedule block");
  return res.json();
}

export async function deleteScheduleBlock(blockId: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/schedule/${blockId}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete schedule block");
}

export async function completeScheduleBlock(blockId: string): Promise<ScheduleBlockCompleteResult> {
  const res = await authFetch(`${API_BASE}/schedule/${blockId}/complete`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to complete schedule block");
  return res.json();
}

export interface LifeAreaBreakdown {
  area: string;
  label: string;
  minutes: number;
  block_count: number;
  color: string;
}

export interface AutoPlanResponse {
  blocks: ScheduleBlock[];
  date: string;
  total: number;
  summary: string;
  focus_block_id?: string | null;
  total_focus_minutes: number;
  total_break_minutes: number;
  life_area_breakdown: LifeAreaBreakdown[];
}

export async function generateSchedule(body: {
  date?: string;
  preferences?: Record<string, unknown>;
  preview?: boolean;
  commitment_level?: CommitmentLevel;
  /**
   * Per-area weekly minute caps (FR-1, FR-18). Forwarded to the deterministic
   * cap inside generate_schedule. 0 means "do not schedule this area" — the
   * frontend MUST pass it through unchanged. NULL/undefined means "no opinion".
   */
  life_area_commits?: Record<string, number>;
} = {}): Promise<AutoPlanResponse> {
  const res = await authFetch(`${API_BASE}/schedule/generate`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to generate schedule");
  return res.json();
}

export async function replanSchedule(date: string, skippedBlockId?: string): Promise<ScheduleResponse> {
  const res = await authFetch(`${API_BASE}/schedule/replan`, {
    method: "POST",
    body: JSON.stringify({ date, skipped_block_id: skippedBlockId }),
  });
  if (!res.ok) throw new Error("Failed to replan schedule");
  return res.json();
}

export interface WeeklyPlanTheme {
  day: string;
  theme: string;
  focus: string;
}

export interface WeeklyPlanResponse {
  themes: WeeklyPlanTheme[];
  total_pending: number;
  active_goals: number;
}

export async function getWeeklyPlan(): Promise<WeeklyPlanResponse> {
  const res = await authFetch(`${API_BASE}/schedule/weekly-plan`, { method: "POST" });
  if (!res.ok) throw new Error("Failed to generate weekly plan");
  return res.json();
}

export async function syncScheduleToGoogle(date?: string): Promise<GoogleScheduleSyncResult> {
  const res = await authFetch(`${API_BASE}/schedule/sync/google`, {
    method: "POST",
    body: JSON.stringify(date ? { date } : {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || "Failed to sync timetable");
  }
  return res.json();
}

// ── Voice Input API ──────────────────────────────────
export interface VoiceInputResult {
  success: boolean;
  transcript: string;
  processed: boolean;
  result?: {
    response: string;
    agent: string;
    tokens_used: number;
  };
  message?: string;
}

export async function voiceInput(audioBlob: Blob, filename?: string): Promise<VoiceInputResult> {
  const token = getAccessToken();
  const formData = new FormData();
  formData.append("file", audioBlob, filename || "recording.webm");

  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  // Note: do NOT set Content-Type — browser sets multipart boundary automatically
  const res = await fetch(`${API_BASE}/voice`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Voice input failed" }));
    throw new Error(err.detail || "Voice input failed");
  }
  return res.json();
}

// ── Goal Recommendations API ─────────────────────────
export interface GoalRecommendation {
  id: string;
  content: string;
  status: string;
  created_at: string | null;
}

export interface GoalRecommendationsResponse {
  recommendations: GoalRecommendation[];
  total: number;
}

export async function getGoalRecommendations(): Promise<GoalRecommendationsResponse> {
  const res = await authFetch(`${API_BASE}/recommendations/goals`);
  if (!res.ok) throw new Error("Failed to load goal recommendations");
  return res.json();
}

export async function acceptGoalRecommendation(
  recId: string
): Promise<{ success: boolean; goal_id: string; title: string }> {
  const res = await authFetch(`${API_BASE}/recommendations/${recId}/accept`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to accept recommendation");
  return res.json();
}

/**
 * Atomic Reflect "Add as task" flow: creates the Task linked to the
 * recommendation's goal AND flips the rec to status='accepted' in a
 * single backend transaction. Replaces the previous two-step flow
 * (POST /api/tasks → POST mark-accepted) which could leave a
 * half-state on tab navigation (UAT 2026-05-07, P15).
 *
 * Uses `keepalive: true` so a request fired right before navigation
 * still completes server-side. The fetch promise itself may reject
 * after navigation, but the write is durable.
 */
export async function acceptRecommendationAsTask(
  recId: string,
  body: { title: string; description?: string; priority?: number }
): Promise<TaskItem> {
  const res = await authFetch(
    `${API_BASE}/recommendations/${recId}/accept-as-task`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }
  );
  if (!res.ok) throw new Error("Failed to accept recommendation as task");
  return res.json();
}

export async function dismissGoalRecommendation(
  recId: string
): Promise<{ success: boolean }> {
  const res = await authFetch(`${API_BASE}/recommendations/${recId}/dismiss`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to dismiss recommendation");
  return res.json();
}

// ── Life Areas (Phase 9-5) ───────────────────────────
export interface LifeAreaItem {
  id: string;
  area: string;
  label: string;
  emoji: string;
  satisfaction: number;
  focus_level: string;
  focus_score: number;
  goal_count: number;
  avg_progress: number;
  /** User-set weekly minute commitment (FR-1, FR-18). NULL = use computed default. */
  target_minutes_per_week: number | null;
}

export interface LifeAreaListResponse {
  areas: LifeAreaItem[];
}

export async function getLifeAreas(): Promise<LifeAreaListResponse> {
  const res = await authFetch(`${API_BASE}/life-areas`);
  if (!res.ok) throw new Error("Failed to load life areas");
  return res.json();
}

export async function updateLifeArea(
  areaId: string,
  body: { satisfaction?: number; focus_level?: string; focus_score?: number }
): Promise<LifeAreaItem> {
  const res = await authFetch(`${API_BASE}/life-areas/${areaId}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Failed to update life area");
  return res.json();
}

// ── Current Recommendation (Phase 9-2) ──────────────
export interface InferredRecommendationSchedule {
  duration_minutes: number;          // 5..480
  target_date?: string | null;       // YYYY-MM-DD; nullable
  preferred_start_hour?: number | null;  // 0..23; nullable
  label?: string | null;             // human-readable, e.g. "Tuesday morning"
}

export interface CurrentRecommendation {
  id: string;
  content: string;
  type: string;
  goal_id: string | null;
  goal_title: string | null;
  roadmap_step: string | null;
  roadmap_step_status: string | null;
  reason: string | null;
  /** Time-bound rec hint (added 2026-05-06). Present when the engine
   *  emitted a duration / preferred slot. The Reflect "Try this" flow
   *  uses this to offer "Schedule it" alongside "Add as task". */
  inferred_schedule?: InferredRecommendationSchedule | null;
  created_at: string | null;
}

export async function getCurrentRecommendation(): Promise<CurrentRecommendation> {
  const res = await authFetch(`${API_BASE}/recommendations/current`);
  if (!res.ok) throw new Error("Failed to load current recommendation");
  return res.json();
}

// ── Push Notification API ────────────────────────────
export async function getVapidKey(): Promise<string> {
  const res = await fetch(`${API_BASE}/push/vapid-key`);
  if (!res.ok) throw new Error("Push notifications not available");
  const data = await res.json();
  return data.public_key;
}

export async function subscribePush(subscription: PushSubscription): Promise<void> {
  const json = subscription.toJSON();
  const res = await authFetch(`${API_BASE}/push/subscribe`, {
    method: "POST",
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      user_agent: navigator.userAgent,
    }),
  });
  if (!res.ok) throw new Error("Failed to subscribe to push notifications");
}

export async function unsubscribePush(endpoint: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/push/unsubscribe`, {
    method: "POST",
    body: JSON.stringify({ endpoint }),
  });
  if (!res.ok) throw new Error("Failed to unsubscribe from push notifications");
}


// ── Decision Engine ───────────────────────────────────
export interface DecisionOption {
  name: string;
  score?: number | null;
  judgment?: string | null;
  pros: string[];
  cons: string[];
}

export interface Decision {
  id: string;
  question: string;
  context: string | null;
  status: string;
  options: DecisionOption[];
  ai_recommendation: string | null;
  ai_confidence: number | null;
  ai_reasoning: string | null;
  decision_factors: string[];
  what_if_delay: string | null;
  chosen_option: string | null;
  created_at: string;
  decided_at: string | null;
}

export interface DecisionListResponse {
  decisions: Decision[];
  total: number;
}

export async function createDecision(
  question: string,
  context: string | null,
  options: { name: string }[]
): Promise<Decision> {
  const res = await authFetch(`${API_BASE}/decisions`, {
    method: "POST",
    body: JSON.stringify({ question, context, options }),
  });
  if (!res.ok) throw new Error("Failed to create decision");
  return normalizeDecision((await res.json()) as Decision);
}

export async function listDecisions(
  status: string = "all"
): Promise<DecisionListResponse> {
  const res = await authFetch(`${API_BASE}/decisions?status=${status}`);
  if (!res.ok) throw new Error("Failed to load decisions");
  const data = (await res.json()) as DecisionListResponse;
  return {
    ...data,
    decisions: data.decisions.map(normalizeDecision),
  };
}

export async function analyzeDecision(id: string): Promise<Decision> {
  const res = await authFetch(`${API_BASE}/decisions/${id}/analyze`, {
    method: "POST",
  });
  if (!res.ok) throw new Error("Failed to analyze decision");
  return normalizeDecision((await res.json()) as Decision);
}

export async function chooseDecision(
  id: string,
  chosenOption: string
): Promise<Decision> {
  const res = await authFetch(`${API_BASE}/decisions/${id}/choose`, {
    method: "PUT",
    body: JSON.stringify({ chosen_option: chosenOption }),
  });
  if (!res.ok) throw new Error("Failed to choose decision");
  return normalizeDecision((await res.json()) as Decision);
}

export async function archiveDecision(id: string): Promise<Decision> {
  const res = await authFetch(`${API_BASE}/decisions/${id}/archive`, {
    method: "PUT",
  });
  if (!res.ok) throw new Error("Failed to archive decision");
  return normalizeDecision((await res.json()) as Decision);
}

export async function deleteDecision(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/decisions/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete decision");
}

// ── Goal Matching ──────────────────────────────────────
export interface GoalMatchResult {
  goal_id: string | null;
  goal_title: string | null;
  category: string | null;
  confidence: number;
}

export async function matchTextToGoal(text: string): Promise<GoalMatchResult> {
  const res = await authFetch(`${API_BASE}/goals/match`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
  if (!res.ok) return { goal_id: null, goal_title: null, category: null, confidence: 0 };
  return res.json();
}
